const TOKEN_REFRESH_THRESHOLD_MS = 60_000

interface OtpCredentials {
  key: string
  otp: string
}

interface RefreshResponse {
  token: string
}

type RefreshToken = (key: string, otp: string) => Promise<RefreshResponse>

let selfAppliedToken = ''
let otpCredentials: OtpCredentials | undefined

export class SelfAppliedSessionExpiredError extends Error {
  constructor(cause: unknown) {
    super('Self-applied session could not be renewed', { cause })
    this.name = 'SelfAppliedSessionExpiredError'
  }
}

function getTokenExpiration(token: string): number | undefined {
  try {
    const payload = (token.split('.')[1] ?? '')
      .replaceAll('-', '+')
      .replaceAll('_', '/')
    const { exp } = JSON.parse(atob(payload)) as { exp?: unknown }

    return typeof exp === 'number' ? exp * 1_000 : undefined
  } catch {
    return undefined
  }
}

function isTokenExpiring(token: string) {
  const expiration = getTokenExpiration(token)

  return (
    expiration == null || expiration - Date.now() <= TOKEN_REFRESH_THRESHOLD_MS
  )
}

export const getSelfAppliedToken = () => selfAppliedToken

export function setSelfAppliedToken(token: string) {
  selfAppliedToken = token
  otpCredentials = undefined
}

export function setSelfAppliedOtpSession(
  token: string,
  key: string,
  otp: string
) {
  selfAppliedToken = token
  otpCredentials = { key, otp }
}

export function getSelfAppliedOtp(key: string) {
  return otpCredentials?.key === key ? otpCredentials.otp : undefined
}

export async function refreshSelfAppliedTokenIfNeeded(
  refreshToken: RefreshToken
) {
  const credentials = otpCredentials
  if (credentials == null || !isTokenExpiring(selfAppliedToken)) {
    return selfAppliedToken
  }

  try {
    const { token } = await refreshToken(credentials.key, credentials.otp)
    if (otpCredentials === credentials) selfAppliedToken = token
    return token
  } catch (error) {
    throw new SelfAppliedSessionExpiredError(error)
  }
}
